// How a notification is shown: its icon, and the line under its title. Each
// sender registers its own presentation, and what its messages make stale in
// the client; a source with none shows the bell and the notification's body.

import type { ReactNode } from "react";
import type { AppNotification } from "@/notifications/model/wire.js";
import { IconBell } from "@/platform/ui";

export interface NotificationPresenter {
  /** The sender's id, as it sends it. */
  source: string;
  icon(notification: AppNotification, size: number): ReactNode;
  /** The line under the title; the body when absent or null. */
  detail?(notification: AppNotification, tz: string): string | null;
  /** The query keys a message from this sender makes stale: what changed on
   *  the server when it was sent. */
  refreshes?: readonly (readonly unknown[])[];
}

const presenters = new Map<string, NotificationPresenter>();

/** Register how each sender's notifications are shown. */
export function registerNotificationPresenters(list: readonly NotificationPresenter[]): void {
  for (const presenter of list) presenters.set(presenter.source, presenter);
}

export function NotificationIcon({
  notification,
  size = 18,
}: {
  notification: AppNotification;
  size?: number;
}) {
  return presenters.get(notification.source)?.icon(notification, size) ?? <IconBell size={size} />;
}

export const notificationDetail = (n: AppNotification, tz: string): string | null =>
  presenters.get(n.source)?.detail?.(n, tz) ?? n.body;

/** Unread and read are a whole-row tonal pair: icon, title and body together. */
export const notificationTones = (unread: boolean) =>
  unread
    ? { icon: "text-ink-soft", title: "text-ink", body: "text-ink-soft" }
    : { icon: "text-faint", title: "text-ink-soft", body: "text-faint" };

export function UnreadDot() {
  return (
    <span role="img" aria-label="Unread" className="h-1.5 w-1.5 flex-none rounded-full bg-accent" />
  );
}

export const refreshesOf = (source: string | null): readonly (readonly unknown[])[] =>
  (source != null ? presenters.get(source)?.refreshes : undefined) ?? [];

export const everyRefresh = (): readonly (readonly unknown[])[] =>
  [...presenters.values()].flatMap((p) => p.refreshes ?? []);
